<script setup lang="ts">
import ShowMore from '@/components/ShowMore/ShowMore.vue';
import { ref, onMounted, onBeforeUnmount } from 'vue';
import Loader from '@/components/Loader/Loader.vue';

const sections = [
  { title: 'Unicheck', path: '/soup/p16unicheck/soup.json' },
  { title: 'Measurement Service', path: '/soup/p22measurementservice/soup.json' },
  { title: 'Unicheck Backend', path: '/soup/p340appzeroreferenceapp/soup.json' },
  { title: 'Frontend', path: '/soup/p36ptw100frontendvue3/soup.json' },
];

const software = ref<
  Array<{
    name: string;
    license: { id: string; text: string; url: string };
    version: string;
    download_url: string;
  }>
>([]);

const tableContainer = ref<HTMLElement | null>(null);
const tableHeight = ref(400);
const loading = ref(false);

function updateTableHeight() {
  if (tableContainer.value) {
    const top = tableContainer.value.getBoundingClientRect().top;
    tableHeight.value = window.innerHeight - top - 190;
  }
}

async function loadLocalSOUP() {
  loading.value = true;
  try {
    const responses = await Promise.all(sections.map((s) => fetch(s.path)));

    if (responses.some((r) => !r.ok)) {
      throw new Error('Failed to load one or more soup.json files');
    }
    const dataArray = await Promise.all(responses.map((r) => r.json()));
    software.value = dataArray.flatMap((data) => data.components);
    loading.value = false;
    updateTableHeight();
  } catch (error) {
    console.error('Error loading soup.json:', error);
  }
}

onMounted(() => {
  updateTableHeight();
  window.addEventListener('resize', updateTableHeight);
  loadLocalSOUP();
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateTableHeight);
});
</script>

<template>
  <div v-if="loading" style="min-height: 2000px">
    <Loader type="content" />
  </div>
  <div v-else ref="tableContainer" class="container">
    <v-virtual-scroll :height="tableHeight" :items="software">
      <template #default="{ item }">
        <div class="mb-4 row">
          <div class="d-flex justify-space-between mb-2">
            <component
              :is="item.download_url ? 'a' : 'p'"
              v-bind="
                item.download_url
                  ? { href: item.download_url, target: '_blank', rel: 'noopener' }
                  : {}
              "
            >
              <strong class="font-weight-bold">{{ item.name }}</strong>
              <small v-if="item.version" class="text-caption"> v{{ item.version }}</small>
            </component>

            <component
              :is="item.license.url ? 'a' : 'p'"
              v-bind="
                item.license.url
                  ? { href: item.license.url, target: '_blank', rel: 'noopener' }
                  : {}
              "
              class="ml-4"
            >
              {{ item.license.id }}
            </component>
          </div>
          <ShowMore
            class="w-100"
            :moreLabel="$t('ShowLicenseDetails')"
            :lessLabel="$t('HideLicenseDetails')"
            :collapsedHeight="0"
            buttonPosition="top"
          >
            <div class="mt-4 text-caption">{{ item.license.text }}</div>
          </ShowMore>
        </div>
      </template>
    </v-virtual-scroll>
  </div>
</template>

<style lang="scss" scoped>
.container {
  white-space: pre-wrap;

  overflow-wrap: anywhere;
  word-break: break-word;

  &:deep(.v-btn) {
    margin: 0 !important;
    font-size: 10px !important;
  }
}

.row {
  background-color: rgb(var(--v-theme-secondary-lighten-5));
  padding: 10px;
  border-radius: 8px;
}
</style>
